import { PaginationItem, PaginationItemType } from './types'

const PAGINATION_DEFAULT_BOUNDARY_COUNT = 1
const PAGINATION_DEFAULT_SIBLING_COUNT = 1
const PAGINATION_DEFAULT_CONDENSED_SIBLING_COUNT = 0
const PAGINATION_COMPACT_MAX_EXTRA_SLOTS = 3

const ascending = (a: number, b: number) => a - b

type CreatePaginationItemsParams = {
  currentPage: number
  totalPages: number
  siblingCount?: number
  condensedSiblingCount?: number
  boundaryCount?: number
}

const mergeSortedPagesWithEllipses = (
  sortedPages: number[],
): PaginationItem[] => {
  const items: PaginationItem[] = []
  let previous = 0

  for (const page of sortedPages) {
    if (previous > 0 && page - previous > 1) {
      items.push({
        type: PaginationItemType.Ellipsis,
        key: `gap-${previous}-${page}`,
      })
    }
    items.push({ type: PaginationItemType.Page, page })
    previous = page
  }

  return items
}

const collectWindowedPageNumbers = (
  currentPage: number,
  totalPages: number,
  siblingCount: number,
  boundaryCount: number,
): number[] => {
  const pages = new Set<number>()
  const addPagesInRange = (start: number, end: number) => {
    for (let i = start; i <= end; i += 1) {
      pages.add(i)
    }
  }

  addPagesInRange(1, Math.min(boundaryCount, totalPages))
  addPagesInRange(Math.max(totalPages - boundaryCount + 1, 1), totalPages)
  addPagesInRange(
    Math.max(currentPage - siblingCount, 1),
    Math.min(currentPage + siblingCount, totalPages),
  )

  return [...pages].sort(ascending)
}

const allPagesAsItems = (totalPages: number): PaginationItem[] =>
  Array.from({ length: totalPages }, (_, index) => ({
    type: PaginationItemType.Page,
    page: index + 1,
  }))

const getMaxCompactSize = (
  siblingCount: number,
  boundaryCount: number,
): number =>
  boundaryCount * 2 + siblingCount * 2 + PAGINATION_COMPACT_MAX_EXTRA_SLOTS

const isPageHiddenInCondensedView = (
  page: number,
  currentPage: number,
  totalPages: number,
  condensedSiblingCount: number,
  boundaryCount: number,
): boolean => {
  const condensedPages = new Set(
    collectWindowedPageNumbers(
      currentPage,
      totalPages,
      condensedSiblingCount,
      boundaryCount,
    ),
  )

  return !condensedPages.has(page)
}

const enrichItemsForCondensedView = (
  items: PaginationItem[],
  currentPage: number,
  totalPages: number,
  condensedSiblingCount: number,
  boundaryCount: number,
): PaginationItem[] => {
  const enriched: PaginationItem[] = []
  let lastVisibleCondensedPage: number | null = null
  let lastVisibleCondensedItemIndex = -1

  items.forEach((item, index) => {
    if (item.type === PaginationItemType.Ellipsis) {
      enriched.push(item)
      return
    }

    const hiddenInCondensedView = isPageHiddenInCondensedView(
      item.page,
      currentPage,
      totalPages,
      condensedSiblingCount,
      boundaryCount,
    )

    if (!hiddenInCondensedView) {
      if (
        lastVisibleCondensedPage !== null &&
        item.page - lastVisibleCondensedPage > 1
      ) {
        const hasEllipsisBetween = items
          .slice(lastVisibleCondensedItemIndex + 1, index)
          .some(betweenItem => betweenItem.type === PaginationItemType.Ellipsis)

        if (!hasEllipsisBetween) {
          enriched.push({
            type: PaginationItemType.Ellipsis,
            key: `condensed-${lastVisibleCondensedPage}-${item.page}`,
            condensedViewOnly: true,
          })
        }
      }

      lastVisibleCondensedPage = item.page
      lastVisibleCondensedItemIndex = index
    }

    enriched.push({
      ...item,
      ...(hiddenInCondensedView ? { hiddenInCondensedView: true } : {}),
    })
  })

  return enriched
}

export const createPaginationItems = ({
  currentPage,
  totalPages,
  siblingCount: siblingCountOverride,
  condensedSiblingCount: condensedSiblingCountOverride,
  boundaryCount: boundaryCountOverride,
}: CreatePaginationItemsParams): PaginationItem[] => {
  const siblingCount = siblingCountOverride ?? PAGINATION_DEFAULT_SIBLING_COUNT
  const condensedSiblingCount =
    condensedSiblingCountOverride ?? PAGINATION_DEFAULT_CONDENSED_SIBLING_COUNT
  const boundaryCount =
    boundaryCountOverride ?? PAGINATION_DEFAULT_BOUNDARY_COUNT

  if (totalPages === 1) {
    return [{ type: PaginationItemType.Page, page: 1 }]
  }

  const maxCompactSize = getMaxCompactSize(siblingCount, boundaryCount)
  const maxCondensedCompactSize = getMaxCompactSize(
    condensedSiblingCount,
    boundaryCount,
  )

  const items =
    totalPages <= maxCompactSize
      ? allPagesAsItems(totalPages)
      : mergeSortedPagesWithEllipses(
          collectWindowedPageNumbers(
            currentPage,
            totalPages,
            siblingCount,
            boundaryCount,
          ),
        )

  if (totalPages <= maxCondensedCompactSize) {
    return items
  }

  return enrichItemsForCondensedView(
    items,
    currentPage,
    totalPages,
    condensedSiblingCount,
    boundaryCount,
  )
}
